'use client'

import { useState, useEffect, Suspense } from 'react'
import { useSignIn, useClerk } from '@clerk/nextjs'
import { useRouter, useSearchParams } from 'next/navigation'
import Link from 'next/link'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowLeft, ArrowRight, Mail, Lock, CheckCircle2, AlertCircle, Loader2, Eye, EyeOff, Home } from 'lucide-react'
import { GeometricBackground } from '@/components/ui/geometric-background'

function ForgotPasswordForm() {
    const { isLoaded, signIn, setActive } = useSignIn()
    const { signOut, user } = useClerk()
    const router = useRouter()
    const searchParams = useSearchParams()

    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [code, setCode] = useState('')
    const [successfulCreation, setSuccessfulCreation] = useState(false)
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const [autoTriggered, setAutoTriggered] = useState(false)

    // Pre-fill email from query parameters if present
    useEffect(() => {
        const emailParam = searchParams.get('email')
        if (emailParam) {
            setEmail(emailParam)

            // If we have an email and we are not logged in, and not yet in success state, 
            // and clerk is loaded, we can auto-request the reset to make it "immediate"
            if (isLoaded && !user && !successfulCreation && !loading && !autoTriggered) {
                setAutoTriggered(true)
                const autoTrigger = async () => {
                    setLoading(true)
                    setError('')
                    try {
                        await signIn.create({
                            strategy: 'reset_password_email_code',
                            identifier: emailParam,
                        })
                        setSuccessfulCreation(true)
                    } catch (err: any) {
                        console.error('Auto-reset error:', err)
                        setError(err.errors?.[0]?.longMessage || err.message || 'Something went wrong.')
                    } finally {
                        setLoading(false)
                    }
                }
                autoTrigger()
            }
        }
    }, [searchParams, isLoaded, user, autoTriggered])

    const onRequestReset = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!isLoaded) return

        setLoading(true)
        setError('')

        try {
            if (user) {
                const redirectUrl = window.location.origin + window.location.pathname + '?email=' + encodeURIComponent(email)
                await signOut({ redirectUrl })
                return
            }

            await signIn.create({
                strategy: 'reset_password_email_code',
                identifier: email,
            })
            setSuccessfulCreation(true)
        } catch (err: any) {
            console.error('Reset error:', err)
            setError(err.errors?.[0]?.longMessage || err.message || 'Something went wrong. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    const onResetSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!isLoaded) return

        setLoading(true)
        setError('')

        try {
            const result = await signIn.attemptFirstFactor({
                strategy: 'reset_password_email_code',
                code,
                password,
            })

            if (result.status === 'complete') {
                await setActive({ session: result.createdSessionId })
                router.push('/dashboard')
            } else {
                setError('Verification failed. Please try again.')
            }
        } catch (err: any) {
            setError(err.errors[0]?.longMessage || 'Invalid code or password requirements not met.')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="relative z-10 w-full max-w-md">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="rounded-3xl border border-white/[0.08] bg-white/[0.02] backdrop-blur-xl p-8 shadow-2xl"
            >
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-white tracking-tight mb-2">
                        {successfulCreation ? 'Set New Password' : 'Reset Password'}
                    </h1>
                    <p className="text-white/40 text-sm">
                        {successfulCreation
                            ? `Enter the code sent to ${email} and your new password`
                            : 'Enter your email to receive a password reset code'}
                    </p>
                </div>

                <AnimatePresence mode="wait">
                    {!successfulCreation ? (
                        <motion.form
                            key="request-form"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            onSubmit={onRequestReset}
                            className="space-y-5"
                        >
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-medium text-white/50 mb-1.5 ml-1 uppercase tracking-wider">
                                        Email Address
                                    </label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-white/20 group-focus-within:text-primary transition-colors">
                                            <Mail className="w-4 h-4" />
                                        </div>
                                        <input
                                            type="email"
                                            required
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl py-3 pl-11 pr-4 text-white placeholder:text-white/10 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 transition-all font-mono shadow-inner"
                                        />
                                    </div>
                                </div>
                            </div>

                            {error && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs"
                                >
                                    <AlertCircle className="w-4 h-4 shrink-0" />
                                    <span>{error}</span>
                                </motion.div>
                            )}

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full h-12 bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-all active:scale-[0.98] mt-2 shadow-lg shadow-primary/20"
                            >
                                {loading ? (
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                    <>
                                        Send Reset Code
                                        <ArrowRight className="w-4 h-4" />
                                    </>
                                )}
                            </button>

                            <div className="text-center mt-6">
                                <Link href="/auth/login" className="text-white/40 hover:text-white text-xs inline-flex items-center gap-2 transition-colors">
                                    <ArrowLeft className="w-3 h-3" />
                                    Back to Sign In
                                </Link>
                            </div>
                        </motion.form>
                    ) : (
                        <motion.form
                            key="reset-form"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            onSubmit={onResetSubmit}
                            className="space-y-6"
                        >
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-medium text-white/50 mb-1.5 ml-1 uppercase tracking-wider">
                                        Verification Code
                                    </label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-white/20 group-focus-within:text-primary transition-colors">
                                            <CheckCircle2 className="w-4 h-4" />
                                        </div>
                                        <input
                                            type="text"
                                            required
                                            value={code}
                                            onChange={(e) => setCode(e.target.value)}
                                            className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl py-3 pl-11 pr-4 text-white placeholder:text-white/10 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 transition-all text-center tracking-[0.5em] text-xl font-bold font-mono shadow-inner"
                                            maxLength={6}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-white/50 mb-1.5 ml-1 uppercase tracking-wider">
                                        New Password
                                    </label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-white/20 group-focus-within:text-primary transition-colors">
                                            <Lock className="w-4 h-4" />
                                        </div>
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            required
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl py-3 pl-11 pr-12 text-white placeholder:text-white/10 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 transition-all font-mono shadow-inner"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute inset-y-0 right-0 pr-4 flex items-center text-white/20 hover:text-white/40 transition-colors"
                                        >
                                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>

                                    {password && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            className="mt-3 space-y-3 overflow-hidden"
                                        >
                                            <div className="flex gap-1 h-1">
                                                {[1, 2, 3, 4, 5].map((level) => {
                                                    const strength = [
                                                        password.length >= 8,
                                                        /[A-Z]/.test(password),
                                                        /[a-z]/.test(password),
                                                        /[0-9]/.test(password),
                                                        /[^A-Za-z0-9]/.test(password),
                                                    ].filter(Boolean).length

                                                    let color = 'bg-white/5'
                                                    if (level <= strength) {
                                                        if (strength <= 2) color = 'bg-red-500/50'
                                                        else if (strength <= 4) color = 'bg-orange-500/50'
                                                        else color = 'bg-emerald-500/50'
                                                    }

                                                    return (
                                                        <div
                                                            key={level}
                                                            className={`flex-1 rounded-full transition-colors duration-500 ${color}`}
                                                        />
                                                    )
                                                })}
                                            </div>

                                            <div className="grid grid-cols-2 gap-x-4 gap-y-2 px-1">
                                                {[
                                                    { label: '8+ Characters', met: password.length >= 8 },
                                                    { label: 'Uppercase', met: /[A-Z]/.test(password) },
                                                    { label: 'Lowercase', met: /[a-z]/.test(password) },
                                                    { label: 'Number', met: /[0-9]/.test(password) },
                                                    { label: 'Special Char', met: /[^A-Za-z0-9]/.test(password) },
                                                ].map((req) => (
                                                    <div key={req.label} className="flex items-center gap-2">
                                                        {req.met ? (
                                                            <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                                                        ) : (
                                                            <div className="w-3 h-3 rounded-full border border-white/10" />
                                                        )}
                                                        <span className={`text-[10px] transition-colors ${req.met ? 'text-white/60' : 'text-white/20'}`}>
                                                            {req.label}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </motion.div>
                                    )}
                                </div>
                            </div>

                            {error && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs"
                                >
                                    <AlertCircle className="w-4 h-4 shrink-0" />
                                    <span>{error}</span>
                                </motion.div>
                            )}

                            <div className="space-y-3">
                                <button
                                    type="submit"
                                    disabled={
                                        loading ||
                                        !(
                                            password.length >= 8 &&
                                            /[A-Z]/.test(password) &&
                                            /[a-z]/.test(password) &&
                                            /[0-9]/.test(password) &&
                                            /[^A-Za-z0-9]/.test(password)
                                        )
                                    }
                                    className="w-full h-12 bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-all active:scale-[0.98] shadow-lg shadow-primary/20"
                                >
                                    {loading ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                        'Reset Password'
                                    )}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setSuccessfulCreation(false)}
                                    className="w-full h-12 bg-white/[0.03] hover:bg-white/[0.06] text-white/60 hover:text-white rounded-xl font-medium transition-all text-sm"
                                >
                                    Resend Code
                                </button>
                            </div>
                        </motion.form>
                    )}
                </AnimatePresence>
            </motion.div>
        </div>
    )
}

export default function ForgotPasswordPage() {
    return (
        <div className="relative min-h-svh w-full flex items-center justify-center p-6 bg-black overflow-hidden">
            <GeometricBackground />

            {/* Back to Home Button */}
            <div className="absolute top-6 left-6 z-20">
                <Link
                    href="/"
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/[0.03] border border-white/[0.08] text-white/50 hover:text-white hover:bg-white/[0.06] transition-all text-sm font-medium backdrop-blur-sm group"
                >
                    <Home className="w-4 h-4 group-hover:scale-110 transition-transform" />
                    Back to Home
                </Link>
            </div>

            <Suspense fallback={
                <div className="relative z-10 flex flex-col items-center justify-center">
                    <Loader2 className="w-12 h-12 text-primary animate-spin mb-4" />
                    <p className="text-white/40 text-sm font-medium animate-pulse">Initializing reset flow...</p>
                </div>
            }>
                <ForgotPasswordForm />
            </Suspense>
        </div>
    )
}
